import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Filter, Eye, UserCheck, UserX, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { PaginationBar, EmptyState, LoadingBlock } from "@/components/Pagination";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { RowActionsMenu } from "@/components/admin/RowActionsMenu";
import { StatusFilterTabs } from "@/components/admin/StatusFilterTabs";
import { useLifecycleActions } from "@/components/admin/useLifecycleActions";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import { ROLE_LABELS } from "@/constants/roles";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 15;
const STAFF_ROLES = ["super_admin", "hospital_admin", "faculty", "department_head", "student"];
const STATUSES = ["active", "inactive", "suspended", "pending_verification"];

export default function AdminStaffListPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [orgId, setOrgId] = useState("all");
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, status_filter: statusFilter };
      if (search) params.search = search;
      if (role !== "all") params.role = role;
      if (status !== "all") params.status = status;
      if (orgId !== "all") params.organization_id = orgId;
      const { data } = await api.get("/admin/staff", { params });
      setUsers(data.data.users);
      setTotal(data.data.total);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [page, search, role, status, orgId, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const lc = useLifecycleActions({
    basePath: "/admin/staff",
    labelOf: (row) => `"${row.full_name}"`,
    onDone: load,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/organizations");
        setOrgs(data.data.organizations);
      } catch { /* silent */ }
    })();
  }, []);

  const clearFilters = () => { setSearch(""); setRole("all"); setStatus("all"); setOrgId("all"); setPage(1); };

  const applyStatusChange = async () => {
    if (!confirm) return;
    try {
      const body = { account_status: confirm.nextStatus };
      if (confirm.nextStatus === "suspended") body.reason = "Suspended by Steward Super Admin";
      await api.patch(`/admin/staff/${confirm.user.id}/status`, body);
      toast.success(`Status updated to ${confirm.nextStatus.replace(/_/g, " ")}.`);
      setConfirm(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const activeFilters = useMemo(() => [role, status, orgId, search].filter((v) => v && v !== "all").length, [role, status, orgId, search]);

  return (
    <DashboardLayout title="All Staff" breadcrumbs={["Platform", "Staff"]}>
      <div className="space-y-4" data-testid="admin-staff-list">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-3">
            <StatusFilterTabs value={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }} testId="staff-status-filter" />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" placeholder="Search by name, email or employee ID" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="admin-staff-search" />
            </div>
            <Select value={orgId} onValueChange={(v) => { setOrgId(v); setPage(1); }}>
              <SelectTrigger className="w-60" data-testid="admin-staff-org-filter"><SelectValue placeholder="Organization" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All organizations</SelectItem>
                {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.organization_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={role} onValueChange={(v) => { setRole(v); setPage(1); }}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {STAFF_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            {activeFilters > 0 && (
              <button className="text-xs font-semibold text-slate-500 hover:text-[#101876]" onClick={clearFilters}>Clear filters</button>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white">
          {loading ? <LoadingBlock label="Loading staff…" /> : users.length === 0 ? (
            <EmptyState title="No staff match" description="Try clearing filters." icon={Filter} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Staff</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Organization</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Role</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Dept</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Emp. ID</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#101876] text-xs font-bold text-white">
                          {(u.first_name?.[0] || "") + (u.last_name?.[0] || "")}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{u.full_name}</div>
                          <div className="text-xs text-slate-500 truncate">{u.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{u.organization_name || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-700">{ROLE_LABELS[u.role_code] || u.role_code}</TableCell>
                    <TableCell className="text-sm text-slate-700">{u.department_name || "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{u.employee_id || "—"}</TableCell>
                    <TableCell><StatusBadge status={u.deleted_at ? "deleted" : u.account_status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link to={`/app/super-admin/users/${u.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]">
                          <Eye className="h-4 w-4" />
                        </Link>
                        <RowActionsMenu
                          testId={`staff-actions-${u.id}`}
                          status={u.deleted_at ? "deleted" : (u.account_status === "active" ? "active" : "inactive")}
                          onView={() => navigate(`/app/super-admin/users/${u.id}`)}
                          onEdit={() => navigate(`/app/super-admin/users/${u.id}`)}
                          onDeactivate={() => lc.open("deactivate", u)}
                          onReactivate={() => lc.open("reactivate", u)}
                          onDelete={() => lc.open("delete", u)}
                          onRestore={() => lc.open("restore", u)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testKey="admin-staff-pagination" />
        </div>
      </div>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title}
        description={`This action will change status to ${confirm?.nextStatus?.replace(/_/g, " ")}.`}
        confirmLabel={confirm?.confirmLabel}
        destructive={confirm?.destructive}
        onConfirm={applyStatusChange}
        testKey="admin-staff-status-confirm"
      />
      {lc.dialog}
    </DashboardLayout>
  );
}
